// Insurance calculation engine — pure functions, no side effects

// ── Types ────────────────────────────────────────────────────────

export interface FinancialConfig {
  eModal: number;
  feesPercentage: number;
  demolitionPercentage: number;
  gstRate: number;
  firstYearMonths: number;
  demoPlanMonths: number;
  constructionMonths: number;
  inflationYear1: number;
  inflationYear2: number;
  inflationYear3: number;
  inflationYear4: number;
}

export interface AreaRowInput {
  area: number;
  multiple: number;
  rate: number | null; // null = auto-calc from eModal * multiple
  rateOverride: boolean;
  addGst: boolean;
  eyrBuilt: number;
  firstYearMonths: number | null; // null = use financial config
  demoPlanMonths: number | null;
  constructionMonths: number | null;
  inflationYear1Pct: number | null;
  inflationYear2Pct: number | null;
  inflationYear3Pct: number | null;
  inflationYear4Pct: number | null;
  demolitionPercentage: number | null;
  percentagePa: number;
}

export interface FacilityRowInput {
  area: number;
  multiple: number;
  rate: number | null;
  rateOverride: boolean;
  addGst: boolean;
  firstYearMonths: number | null;
  demoPlanMonths: number | null;
  constructionMonths: number | null;
  inflationYear1Pct: number | null;
  inflationYear2Pct: number | null;
  inflationYear3Pct: number | null;
  inflationYear4Pct: number | null;
  demolitionPercentage: number | null;
}

export interface DamageInput {
  damageAmount: number;
  changePercentage: number;
  firstYearMonths: number | null;
  demoPlanMonths: number | null;
  constructionMonths: number | null;
  inflationYear1Pct: number | null;
  inflationYear2Pct: number | null;
  inflationYear3Pct: number | null;
  inflationYear4Pct: number | null;
}

export interface AreaRowCalc {
  rate: number;
  reinstatementExclFees: number;
  reinstatementInclFees: number;
  reinstatementMonths: number;
  inflationAmtYear1: number;
  inflationAmtYear2: number;
  inflationAmtYear3: number;
  inflationAmtYear4: number;
  reinstatementInflationTotal: number;
  demolitionCost: number;
  demolitionRate: number;
  depreciation: number;
  depreciationTotalPct: number;
  drcForFsl: number;
}

export interface FacilityRowCalc {
  rate: number;
  reinstatementExclFees: number;
  reinstatementInclFees: number;
  reinstatementMonths: number;
  inflationAmtYear1: number;
  inflationAmtYear2: number;
  inflationAmtYear3: number;
  inflationAmtYear4: number;
  reinstatementInflationTotal: number;
  demolitionCost: number;
  demolitionRate: number;
}

export interface DamageCalc {
  adjustedDamage: number;
  reinstatementMonths: number;
  inflationAmtYear1: number;
  inflationAmtYear2: number;
  inflationAmtYear3: number;
  inflationAmtYear4: number;
  reinstatementInflationTotal: number;
}

export interface SummaryTotals {
  reinstatementEstimate: number;
  reinstatementEstimateRounded: number;
  reinstatementInflation: number;
  reinstatementInflationRounded: number;
  totalDemolition: number;
  totalDemolitionRounded: number;
  drcForFsl: number;
  drcForFslRounded: number;
  reinstatementPerUnit: number;
}

// ── Primitive Functions ──────────────────────────────────────────

function n(v: number | null | undefined): number {
  if (v == null || !Number.isFinite(v)) return 0;
  return v;
}

export function calcRate(eModal: number, multiple: number): number {
  return n(eModal) * n(multiple);
}

export function calcReinstatementExclFees(area: number, rate: number, addGst: boolean, gstRate: number): number {
  return n(area) * n(rate) * (addGst ? 1 + n(gstRate) / 100 : 1);
}

export function calcReinstatementInclFees(reinstatementExcl: number, feesPercentage: number): number {
  return n(reinstatementExcl) * (1 + n(feesPercentage) / 100);
}

export function calcReinstatementMonths(
  firstYearMonths: number,
  demoPlanMonths: number,
  constructionMonths: number,
): number {
  return n(firstYearMonths) + n(demoPlanMonths) + n(constructionMonths);
}

export function calcInflationYear(
  base: number,
  prevInflationSum: number,
  rate: number,
  reinstatementMonths: number,
  yearNum: 1 | 2 | 3 | 4,
): number {
  const pct = n(rate) / 100;
  const months = n(reinstatementMonths);
  const total = n(base) + n(prevInflationSum);

  if (yearNum === 1) {
    return n(base) * pct;
  }

  const yearStart = (yearNum - 1) * 12;
  const yearEnd = yearNum * 12;

  if (months <= yearStart) return 0;

  if (months >= yearEnd) {
    return total * pct;
  }

  // Pro-rata
  const factor = (months - yearStart) / 12;
  return total * factor * pct;
}

export function calcDemolitionCost(
  reinstatementIncl: number,
  inflationAmtYear1: number,
  demolitionPct: number,
): number {
  return (n(reinstatementIncl) + n(inflationAmtYear1)) * (n(demolitionPct) / 100);
}

export function calcDemolitionRate(demolitionCost: number, area: number): number {
  const a = n(area);
  if (a === 0) return 0;
  return n(demolitionCost) / a;
}

export function calcDepreciation(
  certYear: number,
  eyrBuilt: number,
  percentagePa: number,
  reinstatementExcl: number,
): number {
  const age = n(certYear) - n(eyrBuilt);
  if (age <= 0) return 0;
  return -(age * (n(percentagePa) / 100) * n(reinstatementExcl));
}

export function calcDrcForFsl(reinstatementExcl: number, depreciation: number): number {
  return n(reinstatementExcl) + n(depreciation);
}

export function calcPercentChange(sModal: number, eModal: number): number {
  const s = n(sModal);
  if (s === 0) return 0;
  return (n(eModal) / s - 1) * 100;
}

export function calcAge(certificateDate: string, eyrBuilt: number): number {
  if (!certificateDate) return 0;
  const certYear = new Date(certificateDate).getFullYear();
  if (!Number.isFinite(certYear)) return 0;
  return certYear - n(eyrBuilt);
}

export function calcAdjustedDamage(amount: number, changePercentage: number): number {
  return n(amount) * (1 + n(changePercentage) / 100);
}

// ── Composite Functions ──────────────────────────────────────────

function resolveInflation(
  base: number,
  months: number,
  y1: number,
  y2: number,
  y3: number,
  y4: number,
): { y1: number; y2: number; y3: number; y4: number; total: number } {
  const amt1 = calcInflationYear(base, 0, y1, months, 1);
  const amt2 = calcInflationYear(base, amt1, y2, months, 2);
  const amt3 = calcInflationYear(base, amt1 + amt2, y3, months, 3);
  const amt4 = calcInflationYear(base, amt1 + amt2 + amt3, y4, months, 4);
  return { y1: amt1, y2: amt2, y3: amt3, y4: amt4, total: amt1 + amt2 + amt3 + amt4 };
}

export function calcAreaRow(input: AreaRowInput, config: FinancialConfig, certYear: number): AreaRowCalc {
  const rate = input.rateOverride && input.rate != null ? input.rate : calcRate(config.eModal, n(input.multiple));

  const reinstatementExcl = calcReinstatementExclFees(n(input.area), rate, input.addGst, config.gstRate);
  const reinstatementIncl = calcReinstatementInclFees(reinstatementExcl, config.feesPercentage);

  const firstYr = n(input.firstYearMonths ?? config.firstYearMonths);
  const demoPlan = n(input.demoPlanMonths ?? config.demoPlanMonths);
  const construction = n(input.constructionMonths ?? config.constructionMonths);
  const months = calcReinstatementMonths(firstYr, demoPlan, construction);

  const y1Pct = n(input.inflationYear1Pct ?? config.inflationYear1);
  const y2Pct = n(input.inflationYear2Pct ?? config.inflationYear2);
  const y3Pct = n(input.inflationYear3Pct ?? config.inflationYear3);
  const y4Pct = n(input.inflationYear4Pct ?? config.inflationYear4);

  const inflation = resolveInflation(reinstatementIncl, months, y1Pct, y2Pct, y3Pct, y4Pct);

  const demoPct = n(input.demolitionPercentage ?? config.demolitionPercentage);
  const demoCost = calcDemolitionCost(reinstatementIncl, inflation.y1, demoPct);
  const demoRate = calcDemolitionRate(demoCost, n(input.area));

  const depreciation = calcDepreciation(certYear, n(input.eyrBuilt), n(input.percentagePa), reinstatementExcl);
  const depTotalPct = reinstatementIncl !== 0 ? (-depreciation / reinstatementIncl) * 100 : 0;
  const drc = calcDrcForFsl(reinstatementExcl, depreciation);

  return {
    rate,
    reinstatementExclFees: reinstatementExcl,
    reinstatementInclFees: reinstatementIncl,
    reinstatementMonths: months,
    inflationAmtYear1: inflation.y1,
    inflationAmtYear2: inflation.y2,
    inflationAmtYear3: inflation.y3,
    inflationAmtYear4: inflation.y4,
    reinstatementInflationTotal: inflation.total,
    demolitionCost: demoCost,
    demolitionRate: demoRate,
    depreciation,
    depreciationTotalPct: depTotalPct,
    drcForFsl: drc,
  };
}

export function calcFacilityRow(input: FacilityRowInput, config: FinancialConfig): FacilityRowCalc {
  const rate = input.rateOverride && input.rate != null ? input.rate : calcRate(config.eModal, n(input.multiple));

  const reinstatementExcl = calcReinstatementExclFees(n(input.area), rate, input.addGst, config.gstRate);
  const reinstatementIncl = calcReinstatementInclFees(reinstatementExcl, config.feesPercentage);

  const firstYr = n(input.firstYearMonths ?? config.firstYearMonths);
  const demoPlan = n(input.demoPlanMonths ?? config.demoPlanMonths);
  const construction = n(input.constructionMonths ?? config.constructionMonths);
  const months = calcReinstatementMonths(firstYr, demoPlan, construction);

  const y1Pct = n(input.inflationYear1Pct ?? config.inflationYear1);
  const y2Pct = n(input.inflationYear2Pct ?? config.inflationYear2);
  const y3Pct = n(input.inflationYear3Pct ?? config.inflationYear3);
  const y4Pct = n(input.inflationYear4Pct ?? config.inflationYear4);

  const inflation = resolveInflation(reinstatementIncl, months, y1Pct, y2Pct, y3Pct, y4Pct);

  const demoPct = n(input.demolitionPercentage ?? config.demolitionPercentage);
  const demoCost = calcDemolitionCost(reinstatementIncl, inflation.y1, demoPct);
  const demoRate = calcDemolitionRate(demoCost, n(input.area));

  return {
    rate,
    reinstatementExclFees: reinstatementExcl,
    reinstatementInclFees: reinstatementIncl,
    reinstatementMonths: months,
    inflationAmtYear1: inflation.y1,
    inflationAmtYear2: inflation.y2,
    inflationAmtYear3: inflation.y3,
    inflationAmtYear4: inflation.y4,
    reinstatementInflationTotal: inflation.total,
    demolitionCost: demoCost,
    demolitionRate: demoRate,
  };
}

export function calcDamageProvision(input: DamageInput, config: FinancialConfig): DamageCalc {
  const adjusted = calcAdjustedDamage(n(input.damageAmount), n(input.changePercentage));

  const firstYr = n(input.firstYearMonths ?? config.firstYearMonths);
  const demoPlan = n(input.demoPlanMonths ?? config.demoPlanMonths);
  const construction = n(input.constructionMonths ?? config.constructionMonths);
  const months = calcReinstatementMonths(firstYr, demoPlan, construction);

  const y1Pct = n(input.inflationYear1Pct ?? config.inflationYear1);
  const y2Pct = n(input.inflationYear2Pct ?? config.inflationYear2);
  const y3Pct = n(input.inflationYear3Pct ?? config.inflationYear3);
  const y4Pct = n(input.inflationYear4Pct ?? config.inflationYear4);

  const inflation = resolveInflation(adjusted, months, y1Pct, y2Pct, y3Pct, y4Pct);

  return {
    adjustedDamage: adjusted,
    reinstatementMonths: months,
    inflationAmtYear1: inflation.y1,
    inflationAmtYear2: inflation.y2,
    inflationAmtYear3: inflation.y3,
    inflationAmtYear4: inflation.y4,
    reinstatementInflationTotal: inflation.total,
  };
}

export function roundTo10k(v: number): number {
  return Math.round(v / 10000) * 10000;
}

export function calcSummaryTotals(
  areaCalcs: AreaRowCalc[],
  facilityCalcs: FacilityRowCalc[],
  damageCalc: DamageCalc | null,
  roundingAdjustments: { reinstatement: number; inflation: number; demolition: number; drc: number },
  numberOfUnits: number,
): SummaryTotals {
  let reinstatement = 0;
  let inflation = 0;
  let demolition = 0;
  let drc = 0;

  for (const a of areaCalcs) {
    reinstatement += a.reinstatementInclFees;
    inflation += a.reinstatementInflationTotal;
    demolition += a.demolitionCost;
    drc += a.drcForFsl;
  }

  for (const f of facilityCalcs) {
    reinstatement += f.reinstatementInclFees;
    inflation += f.reinstatementInflationTotal;
    demolition += f.demolitionCost;
  }

  if (damageCalc) {
    reinstatement += damageCalc.adjustedDamage;
    inflation += damageCalc.reinstatementInflationTotal;
  }

  const reinstatementRounded = roundTo10k(reinstatement) + n(roundingAdjustments.reinstatement);
  const inflationRounded = roundTo10k(inflation) + n(roundingAdjustments.inflation);
  const demolitionRounded = roundTo10k(demolition) + n(roundingAdjustments.demolition);
  const drcRounded = roundTo10k(drc) + n(roundingAdjustments.drc);

  const units = n(numberOfUnits) || 1;

  return {
    reinstatementEstimate: reinstatement,
    reinstatementEstimateRounded: reinstatementRounded,
    reinstatementInflation: inflation,
    reinstatementInflationRounded: inflationRounded,
    totalDemolition: demolition,
    totalDemolitionRounded: demolitionRounded,
    drcForFsl: drc,
    drcForFslRounded: drcRounded,
    reinstatementPerUnit: reinstatementRounded / units,
  };
}
